import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import { PageHeader } from "@/components/site/PageHeader";
import { Reveal } from "@/components/site/Reveal";
import { IMAGES, SERVICES } from "@/data/content";
import { UploadPlaceholder } from "@/components/site/UploadPlaceholder";

export default function Services() {
  useSeo({
    title: "Services | Heat Pumps, Mini Splits & HVAC Installation Massachusetts",
    description:
      "Residential HVAC installation in Massachusetts: heat pumps, mini splits, central air, heating systems and full HVAC replacements by Lighthouse Home Services.",
  });

  return (
    <div>
      <PageHeader
        eyebrow="Our Services"
        title={<>Comfort systems, done right.</>}
        intro="We specialize in residential heating and cooling installations — high-efficiency systems tailored to your home and installed with clean, careful workmanship."
        image={IMAGES.acUnit}
      />

      <section className="mx-auto max-w-7xl px-5 md:px-8 py-20 md:py-28 space-y-20 md:space-y-28">
        {SERVICES.map((s, i) => (
          <Reveal key={s.slug}>
            <div className={`grid gap-10 lg:grid-cols-2 items-center ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`} data-testid={`service-${s.slug}`}>
              <div className="overflow-hidden rounded-3xl h-80 md:h-[26rem]">
                {s.image ? (
                  <img src={s.image} alt={`${s.title} — Lighthouse installation`} className="w-full h-full object-cover" />
                ) : (
                  <UploadPlaceholder label={`${s.title} project`} note="Upload your install photo" />
                )}
              </div>
              <div>
                <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">{s.tag}</span>
                <h2 className="mt-3 font-serif text-4xl md:text-5xl text-navy leading-tight">{s.title}</h2>
                <p className="mt-5 text-muted-foreground leading-relaxed">{s.overview}</p>
                <div className="mt-7">
                  <p className="text-xs uppercase tracking-[0.18em] text-navy font-semibold mb-4">Benefits</p>
                  <ul className="grid sm:grid-cols-2 gap-3">
                    {s.benefits.map((b) => (
                      <li key={b} className="flex items-start gap-3 text-sm text-ink/80">
                        <Check className="h-4 w-4 text-gold mt-0.5 shrink-0" /> {b}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="mt-7 rounded-2xl bg-sand p-6">
                  <p className="text-xs uppercase tracking-[0.18em] text-gold font-semibold mb-2">Why choose Lighthouse</p>
                  <p className="text-sm text-ink/80 leading-relaxed">{s.why}</p>
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </section>

      <section className="bg-navy text-white py-20 md:py-28 relative overflow-hidden grain">
        <div className="relative z-[2] mx-auto max-w-4xl px-5 md:px-8 text-center">
          <h2 className="font-serif text-4xl md:text-6xl leading-tight">Not sure which system is right for you?</h2>
          <p className="mt-6 text-white/70 max-w-xl mx-auto">We'll walk your home, listen to your needs, and give you an honest, right-sized recommendation. No pressure — ever.</p>
          <Link to="/contact" className="mt-9 inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-semibold text-navy transition-transform duration-300 hover:scale-105">
            Request a Quote <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
